import * as React from 'react';
import { useSafeIntl } from '@bloomberg/media.i18n';

import { Checkbox, Props as CheckboxProps } from '../Checkbox';
import { useSlug } from '../hooks/useSlug';
import defaultMessages from '../i18n/defaultMessages';
import type { GlobalProps } from '../types';
import { classes, debounce } from '../utils';
import {
  TranslatedMessage,
  useTranslateMessage,
} from '../utils/translate-messages';

import styles from './Privacy.module.css';

const TOS_LINK = 'https://www.bloomberg.com/notices/tos/';
const PRIVACY_LINK = 'https://www.bloomberg.com/notices/';

// ---------------------------------------
// Type definitions. Change to interface if you'd
// rather, it's fine. The power is in your hands.
// But, PLEASE be kind and export it for users.
//
// If it gets long, you can move to an a separate file
// ---------------------------------------

export type PrivacyDisclaimerProps = GlobalProps & {
  text?: string;
  disclaimerLength?: 'short' | 'full';
  // Use ISO-3166 https://www.geonames.org/countries/
  countryCode?: string;
};

export type PrivacyCheckboxProps = GlobalProps & {
  text?: string;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  checkboxProps?: CheckboxProps;
  // your props here
  // the below props are just for demonstration purposes
  checked?: boolean;
  defaultChecked?: boolean;
  countryCode?: string;
  includeTextInHitArea?: boolean;
  showTextWhenConsentRequired?: boolean;
};

export type PrivacyConsentProps = PrivacyCheckboxProps & {
  showTextWhenConsentRequired?: boolean;
};

// height of singlel line text is either 14px or 16px
// we know if it's a bit more (like 22), it's on TWO lines
// or more and should have slightly different flex container
// behavior. 22 is somewhat arbitrary
const TEXT_HEIGHT_THRESHOLD = 22;

export const requiresExplicitConsent = (countryCode?: string): boolean => {
  // By default we'll display a checkbox otherwise don't show checkbox if US, GB, CA, FR, BR, CN.
  if (!countryCode) return true;
  switch (countryCode) {
    case 'US':
    case 'GB':
    case 'CA':
    case 'FR':
    case 'BR':
    case 'CN':
      return false;

    default:
      return true;
  }
};

export const PrivacyConsent = React.forwardRef<
  HTMLDivElement,
  PrivacyConsentProps
>(function PrivacyComponent(props: PrivacyConsentProps, ref) {
  const {
    onChange = undefined,
    checkboxProps = {},
    text: textProp,
    // name and unstyled come from the inherited GlobalProps
    name = 'PrivacyConsent',
    checked,
    defaultChecked,
    unstyled,
    countryCode,
    includeTextInHitArea = false,
    showTextWhenConsentRequired = true,
  } = props;

  const componentName = useSlug(name);
  const translateMessage = useTranslateMessage();
  const text =
    textProp ??
    translateMessage({
      id: 'media-ui-core_offersAndPromotions',
      defaultMessage: defaultMessages['media-ui-core_offersAndPromotions'],
    });
  const checkbox = React.useRef<HTMLInputElement | null>(null);
  const textRef = React.useRef<HTMLParagraphElement | null>(null);
  const [singleLine, setSingleLine] = React.useState(true);

  const measureText = React.useCallback(() => {
    if (!textRef.current) return;

    const { height } = textRef.current.getBoundingClientRect();

    if (height > TEXT_HEIGHT_THRESHOLD) {
      setSingleLine(false);
    } else {
      setSingleLine(true);
    }
  }, []);

  React.useEffect(() => {
    measureText();
    const debounced = debounce(measureText, 100);

    window.addEventListener('resize', debounced, {
      passive: true,
    });

    return () => {
      window.removeEventListener('resize', debounced);
    };
  }, []);

  const clickPass = React.useCallback(() => {
    if (checkbox.current) {
      checkbox.current.click();
    }
  }, []);

  const cx = classes(
    [
      styles.wrapper,
      {
        [styles['tighter-line-height']]: true,
        [styles['single-line']]: singleLine,
        // adding a stable class for testing/debug
        'single-line': singleLine,
        [styles['with-extra-hit-area']]: includeTextInHitArea,
      },
    ],
    // this second optional param
    // handles the merging of user provided
    // classnames and/or the handling of
    // and 'unstyled' props, which bails out of
    // adding component styles
    {
      ...props,
      unstyled,
      name,
    }
  );

  return (
    <div className={cx} ref={ref} data-component={componentName}>
      <>
        {requiresExplicitConsent(countryCode) && (
          <Checkbox
            ref={checkbox}
            size="small"
            className={styles.checkbox}
            checked={checked}
            defaultChecked={defaultChecked}
            onChange={onChange}
            {...checkboxProps}
          />
        )}
        {(requiresExplicitConsent(countryCode) ||
          showTextWhenConsentRequired) && (
          <p
            dangerouslySetInnerHTML={{ __html: text }}
            ref={textRef}
            onClick={includeTextInHitArea ? clickPass : undefined}
          />
        )}
      </>
    </div>
  );
});

export const PrivacyCheckbox = React.forwardRef<
  HTMLDivElement,
  PrivacyCheckboxProps
>(function PrivacyComponent(props: PrivacyCheckboxProps, ref) {
  return (
    <PrivacyConsent
      name="PrivacyPolicy"
      ref={ref}
      {...props}
      showTextWhenConsentRequired={false}
    />
  );
});

export const PrivacyDisclaimer = React.forwardRef<
  HTMLDivElement,
  PrivacyDisclaimerProps
>(function PrivacyComponent(props: PrivacyDisclaimerProps, ref) {
  const {
    name = 'PrivacyShortDisclaimer',
    unstyled,
    className,
    text = '',
    disclaimerLength = 'short',
    countryCode = '',
  } = props;

  const componentName = useSlug(name);
  const { locale } = useSafeIntl();

  const cx = classes(
    [
      // static, applied classes can be added to an array
      styles.wrapper,
    ],

    {
      ...props,
      unstyled,
      className,
    }
  );

  if (!text) {
    const getLocalizedUrl = (url?: string): string => {
      if (!url) {
        return '';
      }

      if (locale && locale === 'ja-JP') {
        return url.replace('bloomberg.com/', 'bloomberg.com/jp/');
      }

      return url;
    };

    // For messages with two links, we need to track which link we're on
    let linkIndex = 0;
    const multiLinkValues = {
      link: (chunks: React.ReactNode) => {
        const urls = [PRIVACY_LINK, TOS_LINK];
        const url = getLocalizedUrl(urls[linkIndex++]);

        // Replace spaces with &nbsp; to maintain backwards compatibility (&nbsp; makes sure the strings are not broken across rows)
        // Convert chunks to string, replace spaces, then use dangerouslySetInnerHTML
        const textContent = React.Children.toArray(chunks).join('');
        const formattedText = textContent.replace(/ /g, '&nbsp;');

        return (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            dangerouslySetInnerHTML={{ __html: formattedText }}
          />
        );
      },
    };

    // Determine which message to show based on country code
    let messageId:
      | 'media-ui-core_policyAgreeUSFull'
      | 'media-ui-core_policyAgreeOffers'
      | 'media-ui-core_policyAgreeShort';
    let defaultMessage: string;

    switch (countryCode) {
      case 'US':
        messageId = 'media-ui-core_policyAgreeUSFull';
        defaultMessage =
          'By continuing, I agree to the <link>Privacy Policy</link> and <link>Terms of Service</link>.';
        break;
      case 'GB':
      case 'CA':
      case 'FR':
      case 'BR':
      case 'CN':
        messageId = 'media-ui-core_policyAgreeOffers';
        defaultMessage =
          'By continuing, I agree that Bloomberg may send me news and offers relating to Bloomberg products. I also acknowledge the <link>Privacy Policy</link> and agree to the <link>Terms of Service</link>.';
        break;
      default:
        // Left here for backwards compatibility- the original version of this component handled the variation based on short/full lengths.
        if (disclaimerLength === 'full') {
          messageId = 'media-ui-core_policyAgreeUSFull';
          defaultMessage =
            'By continuing, I agree to the <link>Privacy Policy</link> and <link>Terms of Service</link>.';
        } else {
          messageId = 'media-ui-core_policyAgreeShort';
          defaultMessage =
            'By submitting my information, I agree to the <link>Privacy Policy</link> and <link>Terms of Service</link>.';
        }
        break;
    }

    return (
      <div className={cx} ref={ref} data-component={componentName}>
        <p>
          <TranslatedMessage
            id={messageId}
            defaultMessage={defaultMessage}
            values={multiLinkValues}
          />
        </p>
      </div>
    );
  }

  // Custom text provided
  return (
    <div className={cx} ref={ref} data-component={componentName}>
      <p
        dangerouslySetInnerHTML={{
          __html: text,
        }}
      />
    </div>
  );
});
